import React, { useMemo } from 'react';
import { Block, BRAND_STYLES, TRACK_HEIGHT_NUM } from '../../styles/timeline/week.style';
import { OverlayEvent } from '../../types/schemas/calendar';
import { fractionalHour, formatTime } from '../../utils/calendarGrid';

interface EventBlockProps {
  event: OverlayEvent;
  onClick?: (id: string) => void;
  activeTz?: string;
}

export const EventBlock: React.FC<EventBlockProps> = ({ event, onClick, activeTz }) => {

  const timeLabel = useMemo(() => {
    const startIso = event.origStart || event.start;
    const endIso = event.origEnd || event.end;
    return `${event.title} (${formatTime(startIso, activeTz)} - ${formatTime(endIso, activeTz)})`;
  }, [event, activeTz]);

  const style = useMemo(() => {
    const startHour = fractionalHour(event.start, activeTz);
    let endHour = fractionalHour(event.end, activeTz);
    if (endHour <= startHour) endHour += 24;

    const brandStyle = BRAND_STYLES[event.kind];
    const isFree = event.kind === 'FREE';

    const css: any = {
      top: `${startHour * TRACK_HEIGHT_NUM}rem`,
      height: `${Math.max((endHour - startHour) * TRACK_HEIGHT_NUM, 0.35)}rem`,
      width: '100%',
      left: 0,
      zIndex: isFree ? 0 : 1,
      background: brandStyle.fill,
      opacity: brandStyle.opacity ?? 1,
      pointerEvents: isFree ? 'none' : 'auto',
      border: isFree ? `2px solid ${BRAND_STYLES.FREE.border ?? 'transparent'}`
                     : `2px solid ${brandStyle.border}`,
      boxSizing: 'border-box',
      backgroundImage: isFree
        ? `repeating-linear-gradient(45deg, rgba(0, 128, 0, 0.6) 0, rgba(0, 128, 0, 0.6) 3px, transparent 3px, transparent 6px)`
        : 'none',
      borderRadius: 3,
    };

    if (event.continuation === 'head') {
      css.borderBottomLeftRadius = 0;
      css.borderBottomRightRadius = 0;
      css.borderBottom = 'none';
    } else if (event.continuation === 'tail') {
      css.borderTopLeftRadius = 0;
      css.borderTopRightRadius = 0;
      css.borderTop = 'none';
    }

    return css;
  }, [event, activeTz]);

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.stopPropagation();
      const rawId = event.id.replace(/(_head|_tail)$/, '').replace(/_\d+$/, '');
      onClick(rawId);
    }
  };

  return (
    <Block css={style} title={event.description} onClick={handleClick}>
      {timeLabel}
    </Block>
  );
};
